/**
 * WHAT A PICKER SAVES WHEN THE REVIEWER LEAVES IT OR PRESSES ENTER (QA
 * 2026-10-04, C1-01, C1-02, C1-03 — `docs/decisions.md` D-295,
 * `docs/app.md` §5 → Pickers).
 *
 * The rule: **a picker saves only what the reviewer chose.**
 *
 * - **Enter** saves the highlighted option, on the first press. After typing,
 *   that is the top match; after the arrow keys, the option they moved to. If
 *   the matches for what was typed are still loading, Enter waits for them and
 *   then saves the top one. With nothing typed and no arrow key, Enter only
 *   closes the list.
 * - **Leaving the field** (Tab, or a click elsewhere) saves an option only when
 *   the reviewer moved to it with the arrow keys, or when it is the one exact
 *   match, or the only match, for what they typed. Anything else puts the
 *   stored value back.
 * - **Space** types a space unless the arrow keys chose an option
 *   (`EntityPicker`'s key handler; Fluent saved the highlighted one).
 * - **An emptied field** is cleared on leaving or Enter, where the picker can
 *   be cleared (the caller passes `onClear`); elsewhere the stored value comes
 *   back.
 *
 * Fluent's Combobox did otherwise: Tab saved whatever option was highlighted,
 * and an empty box highlights the list's first entry, so emptying Company and
 * pressing Tab saved `Altoona ADC Properties Associates` with no message.
 *
 * Its own module because `app/vitest.config.ts` runs with no DOM, so this rule
 * cannot be asserted through the mounted control — the reason `pickerResync.ts`
 * and `saveOnBlur.ts` stand alone too.
 */

interface PickerChoice {
  id: string
  primary: string
  secondary?: string
}

export interface PickerCommitState<T extends PickerChoice = PickerChoice> {
  /** What the box says now. */
  text: string
  /** The reviewer typed since the box last showed a stored value or a pick. */
  typed: boolean
  /** The reviewer moved the highlight with the arrow keys since they last typed. */
  navigated: boolean
  /** The highlighted option's id, if any. */
  activeId: string | null
  /** The options on screen, in the order shown. */
  options: readonly T[]
  /** Whether `options` answer `text` — false while a search is in flight. */
  optionsAnswerText: boolean
  /** The caller lets this picker be emptied (`onClear`). */
  clearable: boolean
  /** The stored value's id, if any. */
  storedId?: string
}

export type PickerOutcome<T extends PickerChoice = PickerChoice> =
  /** Save this option. */
  | { kind: 'commit'; option: T }
  /** Empty the stored value. */
  | { kind: 'clear' }
  /** Change nothing; the box shows the stored value again on leaving. */
  | { kind: 'keep' }
  /** Enter only: the matches for the typed text are still loading. */
  | { kind: 'wait' }

/** Letters and digits only, lower case: `67-518` and `67518` are one ID. */
const compact = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

/** The option's ID: the first part of its second line (`449 · Altoona, PA`). */
function optionId(option: PickerChoice): string | undefined {
  return option.secondary?.split(' · ')[0]
}

/** Whether `option` is exactly what was typed, by name or by ID. */
export function isExactMatch(option: PickerChoice, text: string): boolean {
  const typed = text.trim().toLowerCase()
  if (typed === '') return false
  if (option.primary.trim().toLowerCase() === typed) return true
  const id = optionId(option)
  return id !== undefined && compact(id) !== '' && compact(id) === compact(typed)
}

function emptied<T extends PickerChoice>(s: PickerCommitState<T>): PickerOutcome<T> {
  return s.clearable && s.storedId ? { kind: 'clear' } : { kind: 'keep' }
}

/** Leaving the field: Tab, a click elsewhere, or focus moving any other way. */
export function decideOnLeave<T extends PickerChoice>(s: PickerCommitState<T>): PickerOutcome<T> {
  if (!s.typed && !s.navigated) return { kind: 'keep' }
  if (s.typed && s.text.trim() === '') return emptied(s)
  const answered = !s.typed || s.optionsAnswerText
  if (!answered) return { kind: 'keep' }
  if (s.navigated) {
    const active = s.options.find((o) => o.id === s.activeId)
    if (active) return { kind: 'commit', option: active }
  }
  if (s.typed) {
    const exact = s.options.filter((o) => isExactMatch(o, s.text))
    if (exact.length === 1) return { kind: 'commit', option: exact[0]! }
    if (s.options.length === 1) return { kind: 'commit', option: s.options[0]! }
  }
  return { kind: 'keep' }
}

/** Enter, with the list open or text typed. */
export function decideOnEnter<T extends PickerChoice>(s: PickerCommitState<T>): PickerOutcome<T> {
  if (s.typed && s.text.trim() === '') return emptied(s)
  if (s.typed && !s.optionsAnswerText) return { kind: 'wait' }
  // After typing, the highlight is the top match unless the arrow keys moved it.
  // With neither, the highlight is only where the list opened — its first
  // entry when the stored value is not among the first rows — so Enter just
  // closes the list.
  const highlighted = s.navigated
    ? s.options.find((o) => o.id === s.activeId)
    : s.typed
      ? s.options[0]
      : undefined
  return highlighted ? { kind: 'commit', option: highlighted } : { kind: 'keep' }
}

/**
 * WHETHER THE LIST MUST BE FETCHED UNFILTERED AGAIN (QA 2026-10-04, A-07).
 * Asked when the box stops showing typed text — a pick, a clear, or the stored
 * value put back. The list then has to answer an empty search, as on a fresh
 * open; one still holding the matches for the discarded text showed an empty
 * Vendor box over the two `3 GEMS` vendors, with no "Any vendor".
 *
 * - Never fetched yet: nothing to reset; the first open fetches.
 * - A fetch still out for typed text: reset, so its late answer for the old
 *   text cannot land.
 * - The unfiltered fetch still out: no reset. It is already the answer, and a
 *   second one doubled the first requests of every quick Tab through a row of
 *   pickers (review of A-07).
 * - Nothing out: reset unless the list already answers the empty search. A
 *   debounced search just dropped never reached the server, so it counts as
 *   nothing out.
 */
export function listNeedsReset(state: { fetched: boolean; optionsTerm: string | null; inFlightTerm: string | null }): boolean {
  if (!state.fetched) return false
  if (state.inFlightTerm !== null) return state.inFlightTerm !== ''
  return state.optionsTerm !== ''
}

/** The coding picker that last had focus in a line table: its line and its name. */
export interface CodingFocus {
  line: string
  name: string
}

/**
 * WHERE FOCUS GOES BACK AFTER A REFUSED SAVE (review of C2-06; follow-up, QA
 * 2026-10-04). A refusal remounts the row's pickers, and the remount drops the
 * focus the old box had, so the table puts it back on the new copy of the
 * last coding picker. It must not do that when the reviewer had already left
 * on purpose.
 *
 * - **Entering** an Account or Department remembers it; entering anything else
 *   in the table forgets.
 * - **Leaving** for nothing (`relatedTarget` null) from a box still in the page
 *   is a real departure — a click on blank space, or another window — and
 *   forgets. A remount cannot reach this: React turns its own event dispatch
 *   off while it commits a render to the page, so the blur the browser fires
 *   as the old box is removed never reaches `onBlurCapture`, and the memory is
 *   kept. The `targetConnected` check (a box no longer in the page keeps the
 *   memory) is belt-and-braces for that case. Leaving for another element
 *   keeps the memory too: that element then has focus, and the restore stands
 *   down for it.
 * - **After a remount** focus goes back only when something is remembered and
 *   focus is on nothing (the page body).
 */
export function codingFocusOnEnter(name: string | null, line: string | null | undefined): CodingFocus | null {
  return line && (name === 'Account' || name === 'Department') ? { line, name } : null
}

export function codingFocusOnLeave(
  last: CodingFocus | null,
  leave: { relatedTarget: unknown; targetConnected: boolean },
): CodingFocus | null {
  return leave.relatedTarget === null && leave.targetConnected ? null : last
}

export function codingFocusToRestore(
  last: CodingFocus | null,
  state: { pickerResetNonce: number; focusOnNothing: boolean },
): CodingFocus | null {
  return state.pickerResetNonce !== 0 && state.focusOnNothing ? last : null
}

/**
 * A REFUSED CODING WRITE, TIED TO ITS FIELD (QA 2026-10-04, C1-09). The modal
 * and the queue card show the refusal above the line table as a notification, so it
 * is announced; the picker it was refused on names that message as its
 * description, so the reason is read again with the field. `messageId` is the
 * message element's `id`.
 */
export interface CodingRefusal {
  lineItemId: string
  /** The control's name: `'Account'`, `'Department'`, or a category's label. */
  field: string
  messageId: string
}

/** The `aria-describedby` for one line's picker: the refusal's id on the line and control it was refused on. */
export function refusalFor(refusal: CodingRefusal | undefined, lineItemId: string, field: string): string | undefined {
  return refusal && refusal.lineItemId === lineItemId && refusal.field === field ? refusal.messageId : undefined
}
